import { cn } from '@/lib/utils';
import { Product, ProductVariant } from '@/types/product-type';
import { Pin, PinOff } from 'lucide-react';
import { memo } from 'react';
import { EditPriceButton } from './edit-price-button';
import { ProductThumbnailStatic } from './product-thumbnail';
import { SkuCopyButton } from './sku-copy-button';

export type ProductSaleGridCardProps = {
  product: Product;
  variant: ProductVariant;
  variantId: string;
  sold: number;
  remain: number;
  isPinned: boolean;
  onTogglePin: (id: string, productId: string, pinned: boolean) => void;
  onAdjustIn: (variant: ProductVariant) => void;
  onAdjustOut: (variant: ProductVariant) => void;
  onEditPrice: (variant: ProductVariant) => void;
};

const stockBtnClass =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-600 touch-manipulation';

function ProductSaleGridCardComponent({
  product: p,
  variant: v,
  variantId,
  sold,
  remain,
  isPinned,
  onTogglePin,
  onAdjustIn,
  onAdjustOut,
  onEditPrice,
}: ProductSaleGridCardProps) {
  const out = remain === 0;
  const low = !out && remain <= 3;
  const sku = (v.sku ?? p.code ?? '').trim();
  const variantName = (v.name || v.productName || p.name || '').trim();
  const displayName = variantName || sku;
  const price = (v.price ?? p.price ?? 0).toFixed(2);
  const imageUrl = v.imageUrl || p.imageUrl;
  const progressPct = remain > 0 ? Math.min(100, Math.round((sold / remain) * 100)) : 0;

  return (
    <article
      className={cn(
        'relative flex h-full min-h-0 flex-col overflow-hidden rounded-xl border bg-white shadow-none',
        isPinned ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200/90',
        out && 'opacity-85',
      )}
    >
      <button
        type="button"
        aria-label={isPinned ? 'Unpin' : 'Pin'}
        className={cn(
          'absolute right-1.5 top-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-full border bg-white touch-manipulation',
          isPinned
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : 'border-slate-200 text-slate-500 hover:bg-slate-50',
        )}
        onClick={() => onTogglePin(variantId, p.id, !isPinned)}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
      </button>

      <div className="flex flex-col gap-2 p-2.5 sm:p-3">
        <div className="flex gap-2.5">
          <ProductThumbnailStatic
            imageUrl={imageUrl}
            alt={displayName}
            size="md"
            borderStyle="none"
            className="h-11 w-11 shrink-0 rounded-lg border border-slate-100 bg-slate-50 sm:h-14 sm:w-14"
          />

          <div className="min-w-0 flex-1 pr-7">
            <div className="flex items-start gap-1.5">
              <p
                className="min-w-0 flex-1 text-[11px] font-semibold leading-snug text-slate-900 line-clamp-2"
                title={displayName}
              >
                {displayName}
              </p>
              {(out || low) && (
                <span
                  className={cn(
                    'shrink-0 rounded px-1 py-0.5 text-[9px] font-bold uppercase',
                    out ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800',
                  )}
                >
                  {out ? 'Out' : 'Low'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <SkuCopyButton sku={sku} className="w-full" />
          <EditPriceButton
            price={price}
            onClick={() => onEditPrice(v)}
            className="self-start"
          />
        </div>
      </div>

      <div className="mt-auto shrink-0 border-t border-slate-100 bg-slate-50/80">
        <div className="h-0.5 bg-slate-100">
          <div
            className={cn('h-full', out ? 'bg-red-400' : low ? 'bg-amber-400' : 'bg-emerald-500')}
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="flex items-center justify-between gap-1.5 px-2.5 py-2">
          <button
            type="button"
            className={stockBtnClass}
            onClick={() => onAdjustOut(v)}
            aria-label="Decrease stock"
          >
            <span className="text-sm font-semibold leading-none">−</span>
          </button>

          <div className="flex min-w-0 flex-col items-center">
            <span
              className={cn(
                'text-sm font-bold tabular-nums leading-none',
                out ? 'text-red-600' : low ? 'text-amber-700' : 'text-slate-800',
              )}
            >
              {sold}/{remain}
            </span>
            <span className="mt-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-400">
              sold / stock
            </span>
          </div>

          <button
            type="button"
            className={stockBtnClass}
            onClick={() => onAdjustIn(v)}
            aria-label="Increase stock"
          >
            <span className="text-sm font-semibold leading-none">+</span>
          </button>
        </div>
      </div>
    </article>
  );
}

function gridCardPropsEqual(prev: ProductSaleGridCardProps, next: ProductSaleGridCardProps) {
  return (
    prev.variantId === next.variantId &&
    prev.sold === next.sold &&
    prev.remain === next.remain &&
    prev.isPinned === next.isPinned &&
    prev.variant.price === next.variant.price &&
    prev.variant.imageUrl === next.variant.imageUrl &&
    prev.variant.sku === next.variant.sku &&
    prev.variant.name === next.variant.name &&
    prev.variant.productName === next.variant.productName &&
    prev.product.price === next.product.price &&
    prev.product.imageUrl === next.product.imageUrl &&
    prev.product.code === next.product.code &&
    prev.product.name === next.product.name
  );
}

export const ProductSaleGridCard = memo(ProductSaleGridCardComponent, gridCardPropsEqual);
